import React, { useState } from 'react';
import api from '../services/api';
import toast from 'react-hot-toast';
import { ClipLoader } from 'react-spinners';
import { formatCurrency } from '../utils/formatters';

function SubscriptionPage() {
  const [isLoading, setIsLoading] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const user = JSON.parse(localStorage.getItem('user'));
  const [cpf, setCpf] = useState(user?.cpf || '');
  const [cpfError, setCpfError] = useState('');

  const validateCPF = (cpf) => {
    const cleanCpf = cpf.replace(/\D/g, '');
    if (cleanCpf.length !== 11) return false;
    return true;
  };

  const formatCPF = (value) => {
    const numbers = value.replace(/\D/g, '');
    if (numbers.length <= 3) return numbers;
    if (numbers.length <= 6) return numbers.replace(/(\d{3})(\d{1,3})/, '$1.$2');
    if (numbers.length <= 9) return numbers.replace(/(\d{3})(\d{3})(\d{1,3})/, '$1.$2.$3');
    return numbers.replace(/(\d{3})(\d{3})(\d{3})(\d{1,2})/, '$1.$2.$3-$4');
  };

  const handleCpfChange = (e) => {
    const formatted = formatCPF(e.target.value);
    setCpf(formatted);
    if (formatted.replace(/\D/g, '').length === 11) {
      setCpfError('');
    }
  };

  // If user is already active
  if (user?.subscription_status === 'ACTIVE') {
    return (
      <>
        <div className="mb-8 text-center max-w-2xl mx-auto">
          <h2 className="text-4xl font-bold font-manrope text-slate-900 tracking-tight mb-4">🎉 Você é Premium!</h2>
          <p className="text-lg font-medium text-slate-500">Sua assinatura está ativa e você tem acesso a todos os recursos exclusivos do FinControl.</p>
        </div>

        <div className="max-w-xl mx-auto">
          <div className="bg-white rounded-2xl border border-slate-200 shadow-xl overflow-hidden p-10 text-center relative">
            <div className="absolute top-0 left-0 right-0 h-2 bg-gradient-to-r from-indigo-500 to-purple-500"></div>
            
            <div className="w-24 h-24 mx-auto bg-gradient-to-tr from-yellow-400 to-yellow-300 rounded-full flex items-center justify-center shadow-lg shadow-yellow-200 mb-6">
              <span className="material-symbols-outlined text-4xl text-yellow-800">workspace_premium</span>
            </div>
            
            <h3 className="text-2xl font-bold font-manrope text-slate-900 mb-4">
              Obrigado por ser Premium!
            </h3>
            
            <p className="text-slate-500 text-base mb-8 leading-relaxed">
              Você tem acesso ilimitado a todos os recursos exclusivos, como o Painel Completo de Imposto de Renda e Gestão Avançada de Cartões de Crédito.
            </p>
            
            <div className="bg-slate-50 border border-slate-100 rounded-xl p-5 inline-block w-full">
              <p className="text-slate-600 font-medium">
                Seu plano atual: <strong className="text-primary font-bold">
                  {user?.plan === 'ANNUAL' ? 'Plano Anual' : 'Plano Mensal'}
                </strong>
              </p>
            </div>
          </div>
        </div>
      </>
    );
  }

  const handleSubscribe = async (planType) => {
    const cleanCpf = cpf.replace(/\D/g, '');
    if (!cleanCpf || cleanCpf.length !== 11) {
      setCpfError('CPF inválido. Digite um CPF válido com 11 dígitos.');
      toast.error('CPF inválido. Verifique os dados informados.');
      return;
    }

    if (!validateCPF(cleanCpf)) {
      setCpfError('CPF inválido. Verifique os números digitados.');
      toast.error('CPF inválido.');
      return;
    }

    setIsLoading(true);
    setSelectedPlan(planType);
    const loadingToast = toast.loading('Processando assinatura...');

    try {
      const response = await api.post('/subscriptions/subscribe', {
        plan_type: planType,
        cpf: cleanCpf
      });

      toast.dismiss(loadingToast);
      toast.success('Redirecionando para pagamento...');

      const updatedUser = { ...user, plan: planType, cpf: cleanCpf };
      localStorage.setItem('user', JSON.stringify(updatedUser));

      if (response.data.payment_url) {
        setTimeout(() => {
          window.location.href = response.data.payment_url;
        }, 1000);
      }
    } catch (error) {
      toast.dismiss(loadingToast);
      toast.error(error.response?.data?.message || 'Erro ao gerar checkout de assinatura.');
      setSelectedPlan(null);
    } finally {
      setIsLoading(false);
    }
  };

  const plans = [
    {
      id: 'MONTHLY',
      name: 'Plano Mensal',
      price: 9.90,
      period: 'mês',
      originalPrice: 19.90,
      discount: '50% OFF',
      features: [
        'Gestão completa de Cartões de Crédito',
        'Módulo de Imposto de Renda (IRPF)',
        'Relatórios financeiros detalhados',
        'Categorização automática de despesas',
        'Suporte prioritário 24/7',
        'Backup automático na nuvem'
      ],
      icon: 'event',
      popular: false
    },
    {
      id: 'ANNUAL',
      name: 'Plano Anual',
      price: 59.90,
      period: 'ano',
      originalPrice: 118.80,
      discount: 'ECONOMIZE 50%',
      features: [
        'Todos os benefícios do plano mensal',
        'Acesso antecipado a novos recursos'
      ],
      icon: 'military_tech',
      popular: true,
      badge: 'MAIS POPULAR'
    }
  ];

  return (
    <>
      <div className="mb-12 text-center">
        <h2 className="text-4xl font-extrabold font-manrope text-slate-900 tracking-tight mb-3">Seja Premium</h2>
        <p className="text-lg font-medium text-slate-500">Desbloqueie todo o poder do FinControl para uma gestão financeira impecável.</p>
      </div>

      {/* Card de CPF */}
      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-8 max-w-lg mx-auto mb-12">
        <div className="text-center mb-6">
          <div className="w-16 h-16 bg-slate-50 border border-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
             <span className="material-symbols-outlined text-primary text-3xl">shield_locked</span>
          </div>
          <h3 className="text-xl font-bold font-manrope text-slate-900">Verificação de Segurança</h3>
          <p className="text-sm font-medium text-slate-500 mt-1">É necessário confirmar seu CPF para prosseguir</p>
        </div>

        <div>
          <input
            type="text"
            value={cpf}
            onChange={handleCpfChange}
            placeholder="000.000.000-00"
            maxLength="14"
            className={`w-full bg-slate-50 border rounded-xl px-5 py-4 text-center text-lg tracking-widest font-mono font-bold text-slate-800 outline-none transition-all focus:ring-4 focus:ring-primary/20 ${cpfError ? 'border-red-400 focus:border-red-500' : 'border-slate-200 focus:border-primary'}`}
          />
          {cpfError && (
            <p className="text-center text-error text-xs font-bold mt-3">
              {cpfError}
            </p>
          )}
        </div>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center h-64">
          <ClipLoader color="#3525cd" size={50} />
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl mx-auto">
          {plans.map((plan) => (
            <div
              key={plan.id}
              className={`relative bg-white rounded-3xl border transition-all duration-300 flex flex-col hover:-translate-y-2 group cursor-pointer ${plan.popular ? 'border-primary shadow-xl shadow-primary/10' : 'border-slate-200 shadow-lg shadow-slate-200/50 hover:shadow-xl'}`}
            >
              {plan.popular && (
                <div className="absolute -top-4 left-0 right-0 flex justify-center">
                   <div className="bg-gradient-to-r from-primary to-indigo-500 text-white text-xs font-bold px-4 py-1.5 rounded-full uppercase tracking-widest shadow-md">
                     ✨ {plan.badge}
                   </div>
                </div>
              )}

              <div className="p-8 pb-6 text-center border-b border-slate-100">
                <div className="w-16 h-16 mx-auto bg-indigo-50 text-primary rounded-2xl flex items-center justify-center mb-6 transform group-hover:scale-110 transition-transform">
                   <span className="material-symbols-outlined text-3xl">{plan.icon}</span>
                </div>
                
                <h3 className="text-2xl font-bold font-manrope text-slate-900 mb-2">
                  {plan.name}
                </h3>
                
                <div className="mb-2 flex items-baseline justify-center gap-1">
                  <span className="text-sm font-bold text-slate-400 uppercase">R$</span>
                  <span className="text-5xl font-extrabold text-slate-900 tracking-tight">
                    {plan.price.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                  </span>
                  <span className="text-slate-500 font-medium">/{plan.period}</span>
                </div>
                
                {plan.originalPrice && (
                  <div className="flex flex-col items-center gap-1 mt-3">
                    <span className="line-through text-slate-400 font-medium text-sm">
                      De: {formatCurrency(plan.originalPrice)}
                    </span>
                    <span className="bg-green-100 text-green-700 font-bold px-3 py-1 rounded-full text-xs">
                      {plan.discount}
                    </span>
                  </div>
                )}
              </div>

              <div className="p-8 pt-6 flex-1 flex flex-col">
                <p className="text-[10px] uppercase font-bold text-slate-400 tracking-widest mb-4">Benefícios Inclusos</p>
                <ul className="space-y-4 mb-8 flex-1">
                  {plan.features.map((feature, idx) => (
                    <li key={idx} className="flex items-start text-left gap-3">
                      <span className="material-symbols-outlined text-primary text-xl flex-shrink-0">check_circle</span>
                      <span className="text-slate-600 font-medium text-sm leading-relaxed">{feature}</span>
                    </li>
                  ))}
                </ul>

                <button
                  onClick={() => handleSubscribe(plan.id)}
                  disabled={isLoading && selectedPlan === plan.id}
                  className={`w-full py-4 px-6 rounded-xl font-bold text-white shadow-md transition-all flex items-center justify-center disabled:opacity-75 ${plan.popular ? 'bg-primary hover:bg-indigo-700 hover:shadow-lg' : 'bg-slate-900 hover:bg-slate-800 hover:shadow-lg'}`}
                >
                  {isLoading && selectedPlan === plan.id ? (
                    <div className="flex items-center gap-2">
                      <ClipLoader color="white" size={20} />
                      <span>Processando...</span>
                    </div>
                  ) : (
                    `Assinar ${plan.name}`
                  )}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Trust Badges */}
      <div className="mt-16 text-center">
        <div className="flex flex-wrap justify-center gap-6 md:gap-12 mb-6">
          <div className="flex items-center gap-2 text-slate-500 font-medium">
            <span className="material-symbols-outlined text-primary">verified_user</span>
            <span>Pagamento 100% seguro</span>
          </div>
          <div className="flex items-center gap-2 text-slate-500 font-medium">
            <span className="material-symbols-outlined text-primary">credit_score</span>
            <span>Aceitamos todos os cartões</span>
          </div>
          <div className="flex items-center gap-2 text-slate-500 font-medium">
            <span className="material-symbols-outlined text-primary">event_available</span>
            <span>Cancele quando quiser</span>
          </div>
        </div>
        <p className="text-xs font-medium text-slate-400 flex items-center justify-center gap-1">
          <span className="material-symbols-outlined text-[16px]">lock</span>
          Ambiente seguro e criptografado - Seus dados estão protegidos
        </p>
      </div>
    </>
  );
}

export default SubscriptionPage;